import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input } from '@/components';
import { nullableStringSetter } from '@/helpers';

import { CompanySchema } from '../../types';

export type InvoicingFormFields = {
  registered_name: string | null;
  vat_number: string | null;
  billing_address: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  billing_country: string | null;
  billing_aditional_info: string | null;
  billing_email: string | null;
};

export type InvoicingFormProps = {
  readonly id: string;
  readonly company: CompanySchema;
  readonly onSubmit: (data: InvoicingFormFields) => void;
};

export function InvoicingForm({ id, company, onSubmit }: InvoicingFormProps) {
  const { t } = useTranslation(['settings']);

  const options = useMemo(
    () => ({
      defaultValues: {
        registered_name: company.registered_name ?? null,
        vat_number: company.vat_number ?? null,
        billing_address: company.billing_address ?? null,
        billing_postal_code: company.billing_postal_code ?? null,
        billing_city: company.billing_city ?? null,
        billing_country: company.billing_country ?? null,
        billing_aditional_info: company.billing_aditional_info ?? null,
        billing_email: company.billing_email ?? null,
      },
    }),
    [company]
  );

  return (
    <Form<InvoicingFormFields> id={id} onSubmit={onSubmit} options={options}>
      {({ formState, register }) => (
        <div className="space-y-4">
          <Field id="registered_name" label={t('Registered company name')} classes={{ field: 'space-y-1' }}>
            <Input id="registered_name" registration={register('registered_name', { setValueAs: nullableStringSetter })} />
          </Field>

          <Field id="vat_number" label={t('VAT number')} classes={{ field: 'space-y-1' }}>
            <Input id="vat_number" registration={register('vat_number', { setValueAs: nullableStringSetter })} />
          </Field>

          <Field id="billing_address" label={t('Billing address')} classes={{ field: 'space-y-1' }}>
            <Input
              id="billing_address"
              placeholder={t('Street and number')}
              registration={register('billing_address', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field id="billing_postal_code" label={t('Postal code')} classes={{ field: 'space-y-1' }}>
              <Input
                id="billing_postal_code"
                registration={register('billing_postal_code', { setValueAs: nullableStringSetter })}
              />
            </Field>

            <Field id="billing_city" label={t('City')} classes={{ field: 'space-y-1' }}>
              <Input id="billing_city" registration={register('billing_city', { setValueAs: nullableStringSetter })} />
            </Field>
          </div>

          <Field id="billing_country" label={t('Country')} classes={{ field: 'space-y-1' }}>
            <Input id="billing_country" registration={register('billing_country', { setValueAs: nullableStringSetter })} />
          </Field>

          <Field
            id="billing_email"
            label={t('Billing email')}
            error={formState.errors['billing_email']}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="billing_email"
              type="email"
              color={formState.errors['billing_email'] ? 'error' : 'primary'}
              registration={register('billing_email', {
                setValueAs: nullableStringSetter,
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('Enter a valid email') },
              })}
            />
          </Field>

          <Field id="billing_aditional_info" label={t('Additional info')} classes={{ field: 'space-y-1' }}>
            <Input
              id="billing_aditional_info"
              registration={register('billing_aditional_info', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
